// SIM-2729: the "Last written" cell on a config table row.
//
// The dot is never the only carrier of meaning — every state also has text, and the status word
// reaches the accessible name, because a colour-only indicator fails WCAG 1.4.1. Opens on hover for
// scanning and on click/Enter for keyboard and touch; a clicked-open panel is pinned, so moving the
// pointer away does not close what someone deliberately opened.

import type { RunDot, TableRunStatus } from "@Simplicity-Education/core";
import { Popover, PopoverAnchor, PopoverContent } from "@simplicity/ui";
import { useState } from "react";
import { formatRelativeTime } from "../../../lib/formatRelativeTime";
import { LastGeneratedDetail } from "./LastGeneratedDetail";

const DOT_CLASS: Record<RunDot, string> = {
  fresh: "bg-success",
  // Pulsing: a run is in flight and on time, so the row is actively being fixed.
  running: "bg-success animate-pulse",
  late: "bg-warning",
  failed: "bg-destructive",
  never: "bg-muted-foreground",
  unscheduled: "bg-muted-foreground",
  // SIM-2913. `paused` and `unknown` are deliberately NOT alarm colours: one is
  // off by design, the other means we cannot tell — neither is a defect, and
  // colouring them amber trains people to ignore amber.
  paused: "bg-muted-foreground",
  unknown: "bg-muted-foreground",
  // Declared for exhaustiveness; not emitted until the scope stamp gives a
  // count something to be judged against (SIM-2914).
  empty: "bg-warning",
  suspect: "bg-warning",
};

const DOT_LABEL: Record<RunDot, string> = {
  fresh: "On schedule",
  running: "Run in progress",
  late: "Overdue",
  failed: "Last run failed",
  never: "Never written",
  unscheduled: "Not scheduled",
  paused: "Paused by design",
  unknown: "Cannot be determined",
  empty: "Wrote no rows",
  suspect: "Wrote on time, data looks wrong",
};

function cellText(run: TableRunStatus, nowMs: number): string {
  if (run.lastWrittenAt == null) return run.dot === "unscheduled" ? "—" : "Never";
  return formatRelativeTime(Date.parse(run.lastWrittenAt), nowMs);
}

export function LastGeneratedCell({
  run,
  loaded,
  nowMs,
  lookbackDays,
}: {
  /** Null when no provider is mounted (e.g. the create-connector form). */
  run: TableRunStatus | null;
  loaded: boolean;
  /** The server's reduce-time clock, passed in so render stays pure. */
  nowMs: number;
  lookbackDays: number;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  if (!loaded) {
    return (
      <span className="text-xs text-muted-foreground" aria-label="Loading last written time">
        &hellip;
      </span>
    );
  }
  if (run == null) {
    return <span className="text-xs text-muted-foreground">&mdash;</span>;
  }

  const label = DOT_LABEL[run.dot];
  const text = cellText(run, nowMs);

  function close() {
    setPinned(false);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {/* Deliberately NOT PopoverTrigger: its click handler toggles the
            controlled `open` we already set on hover, so a click on a
            hover-opened panel closed it. Anchor + our own button keeps one
            owner of the state. */}
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${label} — show how this was determined`}
          // min-h-6/px-1 keeps the hit area at the 24x24 WCAG 2.2 target-size
          // floor; the dot plus a short relative time is smaller than that.
          className="-mx-1 inline-flex min-h-6 items-center gap-1.5 rounded-sm px-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(e) => {
            // The row is click-to-expand; opening the panel must not also
            // toggle the row underneath it.
            e.stopPropagation();
            if (pinned) {
              close();
              return;
            }
            setPinned(true);
            setOpen(true);
          }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => {
            if (!pinned) setOpen(false);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (!pinned) setOpen(false);
          }}
        >
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[run.dot]}`}
          />
          <span
            className={
              run.lastWrittenAt == null ? "italic text-muted-foreground" : "text-foreground"
            }
          >
            {text}
          </span>
        </button>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[27rem]"
        // A hover-opened panel must not steal focus, or scanning the table with
        // a mouse would yank the caret out of whatever field was being edited.
        onOpenAutoFocus={(e) => {
          if (!pinned) e.preventDefault();
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => {
          if (!pinned) setOpen(false);
        }}
        onEscapeKeyDown={close}
      >
        <LastGeneratedDetail run={run} nowMs={nowMs} lookbackDays={lookbackDays} />
      </PopoverContent>
    </Popover>
  );
}
